/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from 'next/router';
import axios from "axios";
import Toast from "../util/Toast";
import ButtonWithLoading from "../form-fields/ButtonWithLoading";
import CustomerForm from "../customer/CustomerForm";
import DeleteAccountModal from "../modals/DeleteAccountModal";
import { fetchStates, fetchCities, fetchStreets } from "@/utils/locationService";
import { handleLogout } from '@/utils/auth';

function MyDetails({ userData, refreshUserData, countries }) {
  const t = useTranslations("Account");
  const [toast, setToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [states, setStates] = useState({});
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const router = useRouter();

  const [billingCustomerType, setBillingCustomerType] = useState(
    userData.billing?.customer_type?.trim() || "personal",
  );
  const [billingAddressType, setBillingAddressType] = useState(
    userData.billing?.address_type?.trim() || "apartment",
  );
  const [formData, setFormData] = useState({
    first_name: userData.first_name || "",
    last_name: userData.last_name || "",
    email: userData.email || "",
    current_password: "",
    new_password: "",
    confirm_password: "",
    billing: {
      ...userData.billing,
      customer_type: userData.billing?.customer_type?.trim() || "personal",
      address_type: userData.billing?.address_type?.trim() || "apartment",
      country: userData.billing?.country?.trim() || "IL", // Default to IL if country is not set, TODO: add a default country to the user data
    },
    shipping: {
      ...userData.shipping,
      address_type: userData.shipping?.address_type?.trim() || "apartment",
      country: userData.shipping?.country?.trim() || "IL", // Default to IL if country is not set, TODO: add a default country to the user data
    },
  });

  const updateStates = async (countryCode, address) => {
    const data = await fetchStates(countryCode);
    if (address === "billing") {
      setStates(data);
    }
  };

  const updateCities = useCallback(async (countryCode, address) => {
    const data = await fetchCities(countryCode);
    if (address === "billing") {
      setCities(data);
    }
    return data;
  }, []);

  useEffect(() => {
    if (formData.billing.country) {
      updateStates(formData.billing.country, 'billing');
      updateCities(formData.billing.country, 'billing').then((cityList) => {
        // If city is already set, fetch streets
        if (formData.billing.city) {
          const city = cityList.find(c => c.name === formData.billing.city);
          if (city?.external_id) {
            fetchStreets(city.external_id).then(data => setStreets(data));
          }
        }
      });
    }
  }, [formData.billing.country, formData.billing.city, updateCities]);

  useEffect(() => {
    if (formData.shipping.country) {
      updateStates(formData.shipping.country, 'shipping');
      updateCities(formData.shipping.country, 'shipping');
    }
  }, [formData.shipping.country, updateCities]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');

    if (field) {
      setFormData((prevData) => ({
        ...prevData,
        [section]: {
          ...prevData[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleBillingCityChange = async (e) => {
    const { value } = e.target;
    const city = cities.find(c => c.name === value);

    // Clear the street when city changes
    handleChange({
      target: {
        name: e.target.name.replace('city', 'street'),
        value: '',
      },
    });

    // Immediately clear the streets list to prevent flickering
    setStreets([]);

    if (city?.external_id) {
      const data = await fetchStreets(city.external_id);
      setStreets(data);
    }
    handleChange(e);
  };

  const handleBillingCustomerTypeChange = (e) => {
    setBillingCustomerType(e.target.value);
    handleChange({ target: { name: "billing.customer_type", value: e.target.value } });
  };

  const handleBillingAddressTypeChange = (e) => {
    setBillingAddressType(e.target.value);
    handleChange({ target: { name: "billing.address_type", value: e.target.value } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Validate passwords if any password field is filled
      if (formData.current_password || formData.new_password || formData.confirm_password) {
        if (!formData.current_password) {
          throw new Error(t("current_password_required"));
        }
        if (!formData.new_password) {
          throw new Error(t("new_password_required"));
        }
        if (formData.new_password !== formData.confirm_password) {
          throw new Error(t("passwords_do_not_match"));
        }
        if (formData.new_password.length < 8) {
          throw new Error(t("password_too_short"));
        }
      }

      // Create data object without password fields if they're empty
      const dataToSend = {
        ...formData,
      };

      if (!formData.current_password) {
        delete dataToSend.current_password;
        delete dataToSend.new_password;
        delete dataToSend.confirm_password;
      }

      const response = await axios.put("/api/user", dataToSend, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));

      setToast({ message: t("update_success"), type: "success" });
      refreshUserData(response.data);
    } catch (error) {
      console.error(error);
      // Get the error message key from the response or use the error message
      const errorKey = error.response?.data?.message;
      setToast({
        message: errorKey ? t(errorKey) : (error.message || t("update_error")),
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await axios.delete('/api/user/delete', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      await handleLogout(router);
    } catch (error) {
      console.error('Error deleting account:', error);
      setToast({
        message: error.response?.data?.message || t('error_generic'),
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <section className="p-3">
      <div className="py-8 px-4 mx-auto max-w-2xl lg:py-0">
        <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">
          {t("my_details")}
        </h2>
        <hr className="dotted-spaced my-4" />
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 mb-6 lg:mb-8">
            <CustomerForm
              formData={formData}
              handleChange={handleChange}
              handleCityChange={handleBillingCityChange}
              states={states}
              countries={countries}
              citiesList={cities}
              streetsList={streets}
              errors={{}}
              customerType={billingCustomerType}
              handleCustomerTypeChange={handleBillingCustomerTypeChange}
              addressType={billingAddressType}
              handleAddressTypeChange={handleBillingAddressTypeChange}
              prefix="billing"
              title={t("billing_address")}
            />
          </div>
          <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">
            {t("change_password")}
          </h2>
          <hr className="dotted-spaced my-4" />
          <div className="pt-6 mt-6">
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
              <div className="w-full">
                <label htmlFor="current_password" className="block mb-2 text-sm font-medium text-gray-900">
                  {t("current_password")}
                </label>
                <input
                  type="password"
                  name="current_password"
                  id="current_password"
                  value={formData.current_password}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                />
              </div>
              <div className="w-full">
                <label htmlFor="new_password" className="block mb-2 text-sm font-medium text-gray-900">
                  {t("new_password")}
                </label>
                <input
                  type="password"
                  name="new_password"
                  id="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                />
              </div>
              <div className="w-full">
                <label htmlFor="confirm_password" className="block mb-2 text-sm font-medium text-gray-900">
                  {t("confirm_password")}
                </label>
                <input
                  type="password"
                  name="confirm_password"
                  id="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 sm:mt-6 flex justify-between items-center">
            <ButtonWithLoading
              isLoading={isSaving}
              text={t('update_details')}
              loadingText={t('saving')}
              type="submit"
              data-testid="update-details"
            />
            <ButtonWithLoading
              variant="error"
              text={t('delete_account')}
              onClick={() => setShowDeleteModal(true)}
              isLoading={isDeleting}
              loadingText={t('deleting')}
              className="whitespace-nowrap"
              data-testid="delete-account"
              size="small"
            />
          </div>
        </form>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {showDeleteModal && (
        <DeleteAccountModal
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteModal(false)}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </section>
  );
}

export default MyDetails;
